import React, { useMemo } from 'react'
import { Card, Select, Space, Typography, Button, message } from 'antd'
import { BarChartOutlined, LineChartOutlined, PieChartOutlined, DownloadOutlined } from '@ant-design/icons'
import ReactECharts from 'echarts-for-react'
import { DataRow } from '../types/api'

const { Title } = Typography
const { Option } = Select

interface DataChartProps {
  data: DataRow[]
  title?: string
}

type ChartType = 'bar' | 'line' | 'pie'

const DataChart: React.FC<DataChartProps> = ({ data, title = '数据图表' }) => {
  const [chartType, setChartType] = React.useState<ChartType>('bar')
  const [xAxis, setXAxis] = React.useState<string>('')
  const [yAxis, setYAxis] = React.useState<string>('')

  // 获取可用的字段
  const availableFields = useMemo(() => {
    if (!data || data.length === 0) return []
    return Object.keys(data[0]).filter(key => key !== 'key')
  }, [data])

  // 获取数值字段
  const numericFields = useMemo(() => {
    if (!data || data.length === 0) return []
    return availableFields.filter(field => {
      const value = data[0][field]
      return typeof value === 'number' || !isNaN(Number(value))
    })
  }, [availableFields, data])

  // 获取分类字段
  const categoryFields = useMemo(() => {
    if (!data || data.length === 0) return []
    return availableFields.filter(field => {
      const value = data[0][field]
      return typeof value === 'string' || !numericFields.includes(field)
    })
  }, [availableFields, numericFields, data])

  // 初始化默认字段
  React.useEffect(() => {
    if (categoryFields.length > 0 && !xAxis) {
      setXAxis(categoryFields[0])
    }
    if (numericFields.length > 0 && !yAxis) {
      setYAxis(numericFields[0])
    }
  }, [categoryFields, numericFields, xAxis, yAxis])

  // 生成图表配置
  const chartOption = useMemo(() => {
    if (!data || data.length === 0 || !xAxis || !yAxis) {
      return {}
    }

    const processedData = data.slice(0, 20) // 限制显示前20条数据

    if (chartType === 'pie') {
      const pieData = processedData.map(item => ({
        name: String(item[xAxis] || '未知'),
        value: Number(item[yAxis]) || 0
      }))

      return {
        title: {
          text: title,
          left: 'center'
        },
        tooltip: {
          trigger: 'item',
          formatter: '{a} <br/>{b}: {c} ({d}%)'
        },
        legend: {
          orient: 'vertical',
          left: 'left'
        },
        series: [
          {
            name: yAxis,
            type: 'pie',
            radius: '50%',
            data: pieData,
            emphasis: {
              itemStyle: {
                shadowBlur: 10,
                shadowOffsetX: 0,
                shadowColor: 'rgba(0, 0, 0, 0.5)'
              }
            }
          }
        ]
      }
    }

    // 柱状图和折线图
    const categories = processedData.map(item => String(item[xAxis] || '未知'))
    const values = processedData.map(item => Number(item[yAxis]) || 0)

    return {
      title: {
        text: title,
        left: 'center'
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: categories,
        axisLabel: {
          rotate: categories.some(cat => cat.length > 6) ? 45 : 0,
          interval: 0
        }
      },
      yAxis: {
        type: 'value',
        name: yAxis
      },
      series: [
        {
          name: yAxis,
          type: chartType,
          data: values,
          itemStyle: {
            color: '#1890ff'
          },
          ...(chartType === 'line' && {
            smooth: true,
            symbol: 'circle',
            symbolSize: 6
          })
        }
      ]
    }
  }, [data, chartType, xAxis, yAxis, title])

  const handleExportChart = () => {
    try {
      // 这里可以实现图表导出功能
      message.success('图表导出功能开发中')
    } catch (error) {
      message.error('导出失败')
    }
  }

  if (!data || data.length === 0) {
    return (
      <Card className="chart-container">
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
          <BarChartOutlined style={{ fontSize: 48, marginBottom: 16 }} />
          <div>暂无数据可视化</div>
        </div>
      </Card>
    )
  }

  return (
    <Card 
      className="chart-container"
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={5} style={{ margin: 0 }}>
            <BarChartOutlined style={{ marginRight: 8 }} />
            {title}
          </Title>
          <Button 
            icon={<DownloadOutlined />}
            onClick={handleExportChart}
            size="small"
          >
            导出图表
          </Button>
        </div>
      }
    >
      <div style={{ marginBottom: 16 }}>
        <Space wrap>
          <span>图表类型:</span>
          <Select
            value={chartType}
            onChange={setChartType}
            style={{ width: 120 }}
          >
            <Option value="bar">
              <BarChartOutlined /> 柱状图
            </Option>
            <Option value="line">
              <LineChartOutlined /> 折线图
            </Option>
            <Option value="pie">
              <PieChartOutlined /> 饼图
            </Option>
          </Select>

          <span>X轴:</span>
          <Select
            value={xAxis}
            onChange={setXAxis}
            style={{ width: 150 }}
            placeholder="选择X轴字段"
          >
            {categoryFields.map(field => (
              <Option key={field} value={field}>{field}</Option>
            ))}
          </Select>

          <span>Y轴:</span>
          <Select
            value={yAxis}
            onChange={setYAxis}
            style={{ width: 150 }}
            placeholder="选择Y轴字段"
          >
            {numericFields.map(field => (
              <Option key={field} value={field}>{field}</Option>
            ))}
          </Select>
        </Space>
      </div>

      {Object.keys(chartOption).length > 0 ? (
        <ReactECharts
          option={chartOption}
          style={{ height: '400px' }}
          opts={{ renderer: 'canvas' }}
        />
      ) : (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
          请选择X轴和Y轴字段
        </div>
      )}

      <div style={{ marginTop: 16, fontSize: 12, color: '#666', textAlign: 'center' }}>
        {data.length > 20 && `注：图表仅显示前20条数据，共${data.length}条记录`}
      </div>
    </Card>
  )
}

export default DataChart
